import React from 'react';
import styled from 'styled-components';
import { Avatar, Box, Container, CssBaseline, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ReactElement, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GET_REDIRECT } from 'graphql/auth/queries/authLogin';
import Loading from 'components/Messages/Loading/Loading';
import Error from 'components/Messages/Error/Error';
import { useAuthContext } from 'context/auth.context';
import { GET_TENANTS_FOR_EMAIL } from 'graphql/auth/queries/getTenantsForEmail';
import { themeOptions } from 'theme';
import SingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/SingleSelectDropdown';
import Form from 'components/Form/Form';
import { FormFieldInterface } from 'components/Form/Form.types';
import { LOGIN_LANDING_PAGE } from 'const/const';

const StyledBox = styled(Box)`
  margin-top: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormWrapper = styled.div`
  min-width: 25rem;
  margin-top: -2rem;
`;

interface Tenant {
  id: number;
  name: string;
}

const Login = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const destination = searchParams.get('destination');
  const [email, setEmail] = useState<string>('');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const [noTenantFound, setNoTenantFound] = useState<boolean>(false);
  const [getRedirect, { loading, error, data }] = useLazyQuery(GET_REDIRECT);
  const [getTenantsForEmail] = useLazyQuery(GET_TENANTS_FOR_EMAIL);
  const { isSessionActive } = useAuthContext();
  const navigate = useNavigate();

  if (isSessionActive()) {
    navigate(LOGIN_LANDING_PAGE);
  }

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error.message} />;
  }

  if (data) {
    if (data.getRedirect === '') {
      return <Error message='Tenant not found' />;
    }

    window.location.replace(data.getRedirect);
  }

  const handleSubmit = () => {
    setNoTenantFound(false);

    if (selectedTenant) {
      getRedirect({
        variables: {
          input: {
            tenantId: selectedTenant.id,
            userEmail: email,
            destination,
          },
        },
      });
      return;
    }

    getTenantsForEmail({
      variables: { email },
    }).then((result) => {
      if (!result.data?.getTenantsForEmail.length) {
        setNoTenantFound(true);
        return;
      }

      if (result.data.getTenantsForEmail.length > 1) {
        setTenants(result.data.getTenantsForEmail);
        return;
      }

      if (result.data.getTenantsForEmail.length === 1) {
        getRedirect({
          variables: {
            input: {
              tenantId: result.data.getTenantsForEmail[0].id,
              userEmail: email,
              destination,
            },
          },
        });
      }
    });
  };

  const handleUpdateEmail = (email: string) => {
    setEmail(email);
    setTenants([]);
    setSelectedTenant(undefined);
  };

  const fields: FormFieldInterface[] = [
    {
      label: 'Email',
      onChange: (email) => handleUpdateEmail(email),
      type: 'email',
      value: email,
      containedLabel: true,
    },
    {
      label: 'Tenant',
      onChange: () => {},
      type: 'dropdown',
      customField: (
        <SingleSelectDropdown
          variant='outlined'
          allOptions={tenants}
          selected={selectedTenant}
          fullWidth={true}
          setSelected={(selectedOption?: Tenant) => setSelectedTenant(selectedOption)}
          placeholder='Please Select a Client'
        />
      ),
      hide: !tenants.length,
    },
  ];

  return (
    <Container maxWidth='xs'>
      <CssBaseline />
      <StyledBox>
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Client Log in
        </Typography>
        <FormWrapper>
          <Form fields={fields} buttonText='Login' onSubmit={handleSubmit} />
        </FormWrapper>
        {noTenantFound && (
          <Typography color={themeOptions.colors.error900}>* No Tenant found for Email</Typography>
        )}
      </StyledBox>
    </Container>
  );
};

export default Login;
